<script lang="ts">
  import type { CustomerQuote } from "$lib/api/quotes";
  import { listOrganizationQuotes } from "$lib/api/quotes";
  import { quoteIsAcceptable } from "$lib/billing/quote-status";
  import { track } from "$lib/telemetry/emit";
  import Banner from "$lib/components/Banner.svelte";
  import Button from "$lib/components/Button.svelte";

  const { orgId }: { orgId: string } = $props();
  let quotes = $state<CustomerQuote[]>([]);
  let now = $state(Date.now());
  const openQuotes = $derived(quotes.filter((quote) => quoteIsAcceptable(quote.status, quote.validUntil, now)));

  $effect(() => {
    const id = orgId;
    const controller = new AbortController();
    const viewed = new Set<string>();
    let lastAttempt = 0;
    let loading = false;
    quotes = [];

    async function refresh(): Promise<void> {
      if (loading) return;
      loading = true;
      lastAttempt = Date.now();
      try {
        // Recipient authorization stays on the server; billing.manage is not required.
        const data = await listOrganizationQuotes(id, controller.signal);
        if (controller.signal.aborted) return;
        now = Date.now();
        quotes = data;
        const active = data.filter((quote) => quoteIsAcceptable(quote.status, quote.validUntil, now));
        if (active.some((quote) => !viewed.has(quote.id))) {
          active.forEach((quote) => viewed.add(quote.id));
          track({ name: "billing.quote_notification", action: "viewed", count: active.length });
        }
      } catch {
        if (controller.signal.aborted) return;
        // An optional notification must not block the page or retain an unverified link.
        quotes = [];
        track({ name: "billing.quote_notification", action: "load_failed", count: 0 });
      } finally {
        loading = false;
      }
    }

    function refreshOnReturn(): void {
      if (document.visibilityState !== "hidden" && Date.now() - lastAttempt >= 30_000) void refresh();
    }

    void refresh();
    window.addEventListener("focus", refreshOnReturn);
    document.addEventListener("visibilitychange", refreshOnReturn);
    return () => {
      controller.abort();
      window.removeEventListener("focus", refreshOnReturn);
      document.removeEventListener("visibilitychange", refreshOnReturn);
    };
  });

  $effect(() => {
    if (!openQuotes.length) return;
    const expiresAt = Math.min(...openQuotes.map((quote) => new Date(quote.validUntil).getTime()));
    // Remove expired links even when the tab stays open; no background API polling.
    const timer = window.setTimeout(() => (now = Date.now()), Math.min(2_147_483_647, Math.max(0, expiresAt - Date.now())));
    return () => window.clearTimeout(timer);
  });
</script>

{#snippet reviewButton(quote: CustomerQuote)}
  <Button
    size="sm"
    href={`/orgs/${orgId}/quotes/${quote.id}`}
    ariaLabel={`Review quote: ${quote.title}`}
    onclick={() => track({ name: "billing.quote_notification", action: "opened", count: openQuotes.length })}
  >Review quote</Button>
{/snippet}

{#if openQuotes.length === 1}
  {@const quote = openQuotes[0]!}
  <div class="quote-notification">
    <Banner tone="info" title="You have an open quote">
      <span class="quote-title">{quote.title}</span> is ready to review. Compare its options and
      accept the one that works for you.
      {#snippet actions()}{@render reviewButton(quote)}{/snippet}
    </Banner>
  </div>
{:else if openQuotes.length > 1}
  <div class="quote-notification">
    <Banner tone="info" title={`You have ${openQuotes.length} open quotes`}>
      Review each one and accept the option that works for you.
      <ul>
        {#each openQuotes as quote (quote.id)}
          <li>
            <span class="quote-title">{quote.title}</span>
            {@render reviewButton(quote)}
          </li>
        {/each}
      </ul>
    </Banner>
  </div>
{/if}

<style>
  .quote-notification { margin-bottom: 20px; }
  ul { list-style: none; padding: 0; margin: 12px 0 0; display: grid; gap: 10px; }
  li { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px 18px; }
  .quote-title { font-weight: 600; color: var(--heading); overflow-wrap: anywhere; }
</style>
